import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [orgtype, setOrgtype] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [authToken, setAuthToken] = useState(null);

  // Helper function to check token expiration
  const isTokenExpiredFn = (token) => {
    try {
      const { exp } = jwtDecode(token);
      return Date.now() >= exp * 1000;
    } catch (error) {
      console.error("Invalid token format", error);
      return true;
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("fname");
    localStorage.removeItem("userrole");
    localStorage.removeItem("remember_me");
    localStorage.removeItem("organization_id");
    localStorage.removeItem("themeMode");
    setIsAuthenticated(false);
    setIsTokenExpired(false);
    setOrganizationId(null);
    setUserRole(null);
    setUserId(null);
    setPlanId(null);
    setOrgtype(null);
    setAuthToken(null);
  }, []);

  const refreshAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) return null;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/auth/refresh`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to refresh token");

      const data = await response.json();
      const newAccessToken = data.access_token;

      localStorage.setItem("access_token", newAccessToken);
      setAuthToken(newAccessToken);

      const decoded = jwtDecode(newAccessToken);
      setUserRole(decoded.userrole);
      setOrganizationId(decoded.organization_id);
      setUserId(decoded.userId);
      setPlanId(decoded.plan_id);
      setOrgtype(decoded.orgtype);
      setIsAuthenticated(true);
      setIsTokenExpired(false);
      return newAccessToken;
    } catch (error) {
      console.error("Refresh failed", error);
      logout();
      return null;
    }
  }, [logout]);

  // Periodic token expiry check
  useEffect(() => {
    const checkToken = async () => {
      try {
        const jwtToken = localStorage.getItem("access_token");
        const refresh_token = localStorage.getItem("refresh_token");
        const rememberMe = localStorage.getItem("remember_me") === "true";

        if (jwtToken) {
          if (isTokenExpiredFn(jwtToken)) {
            if (rememberMe && refresh_token) {
              const newToken = await refreshAccessToken();
              if (!newToken) {
                logout();
                setIsTokenExpired(true);
                setIsAuthenticated(false);
                setUserRole(null);
                setOrganizationId(null);
                setUserId(null);
                setPlanId(null);
                setOrgtype(null);
                setAuthToken(null);
              }
            } else {
              logout();
              setIsTokenExpired(true);
              setIsAuthenticated(false);
              setUserRole(null);
              setOrganizationId(null);
              setUserId(null);
              setPlanId(null);
              setOrgtype(null);
              setAuthToken(null);
            }
          } else {
            // Decode token to extract the user role directly from the payload
            const decoded = jwtDecode(jwtToken);
            const roleFromToken = decoded.userrole;
            setUserRole(roleFromToken);
            setOrganizationId(decoded.organization_id);
            setUserId(decoded.userid);
            const storedPlanId = localStorage.getItem("plan_id");
            if (storedPlanId) {
              setPlanId(storedPlanId);
            } else {
              setPlanId(decoded.plan_id);
            }
            setOrgtype(decoded.orgtype);
            setAuthToken(jwtToken);
            setIsAuthenticated(true);
          }
        } else {
          logout();
          setIsAuthenticated(false);
          setUserRole(null);
          setOrganizationId(null);
          setUserId(null);
          setPlanId(null);
          setOrgtype(null);
          setAuthToken(null);
        }
      } catch (error) {
        console.error("Error in checkToken:", error);
        logout();
      }
    };

    checkToken(); // Check once when the component mounts
    setLoading(false);

    const interval = setInterval(checkToken, 10000);
    return () => clearInterval(interval); // Cleanup on unmount
  }, [refreshAccessToken, logout]);

  const login = (token, refresh_token, fname) => {
    try {
      if (isTokenExpiredFn(token)) {
        console.log("Cannot log in: Token is expired");
        return;
      }
      // Store the token and optionally the fname in local storage
      localStorage.setItem("access_token", token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("fname", fname);
      setAuthToken(token);

      // Decode the token to get the user role
      const decoded = jwtDecode(token);
      const roleFromToken = decoded.userrole; // Adjust the key as necessary
      setOrganizationId(decoded.organization_id);
      const organization_id = decoded.organization_id; // remove local storage dependencies from components
      localStorage.setItem("organization_id", organization_id); // then remove this
      setUserId(decoded.userid);
      setPlanId(decoded.plan_id);
      setOrgtype(decoded.orgtype);
      setUserRole(roleFromToken);

      setIsAuthenticated(true);
      setIsTokenExpired(false);
    } catch (error) {
      console.error("Error in login:", error);
      logout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        loading,
        isTokenExpired,
        setIsTokenExpired,
        organizationId,
        userId,
        planId,
        setPlanId,
        orgtype,
        userRole,
        authToken,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);